import { RelocationStatus } from "@/gql/graphql";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";
import { RelocationInput } from "@/app/Relocations/Components/RelocationInput";
import { startTransition, Suspense, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { bookingChangeRelocationMutation } from "@/app/Bookings/GraphQL/bookingMutations";
import { BookingRecord } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import { RelocationListItem } from "@/app/Relocations/GraphQL/relocationListQuery";
import { RelocationSummaryCard } from "@/app/Relocations/Components/RelocationSummaryCard";

type BookingChangeRelocationDialogProps = {
  booking: BookingRecord;
  newRelocation: Pick<RelocationListItem, "id"> | null;
};

export const BookingChangeRelocationDialog: DialogComponent<
  BookingChangeRelocationDialogProps,
  RelocationListItem | null
> = ({ booking, newRelocation }) => {
  const [relocation, setRelocation] = useState<Pick<
    RelocationListItem,
    "id"
  > | null>(newRelocation);
  const { mutateAsync } = useGqlMutation(bookingChangeRelocationMutation);
  const { close, resolve } = useDialogState();

  return (
    <BaseDialog title={"Change Relocation"}>
      <div className="mt-12">
        <GenericForm
          initialValues={{
            relocation: {
              connect: newRelocation?.id ?? null!,
            },
            release_linked_relocation: false,
          }}
          onSubmit={async (values) => {
            await mutateAsync({
              id: booking.id,
              input: {
                release_linked_relocation: values.release_linked_relocation,
                relocation_id: values.relocation.connect,
              },
            });
            resolve?.(relocation as RelocationListItem | null);
            close();
          }}
        >
          <RelocationInput
            name="relocation.connect"
            label="Relocation"
            getQueryVariables={(search) => ({
              search,
              page: 1,
              first: 20,
              status: [RelocationStatus.Ready],
            })}
            // A transition keeps the current cards on screen while the newly
            // selected relocation loads, instead of flashing to a fallback.
            onChange={(value) => startTransition(() => setRelocation(value))}
          />
          <SwitchInput
            name={"release_linked_relocation"}
            label={`Relist old relocation (ID: ${booking.relocation.id})`}
          />
          {relocation ? (
            <div className="col-span-full space-y-4">
              <RelocationComparisonCard
                label="Old"
                relocationId={booking.original_relocation_id}
              />
              <RelocationComparisonCard
                label="New"
                relocationId={relocation.id}
              />
            </div>
          ) : null}
          <DialogSubmitButton text={"Change Relocation"} />
        </GenericForm>
      </div>
    </BaseDialog>
  );
};

function RelocationComparisonCard({
  label,
  relocationId,
}: {
  label: string;
  relocationId: string;
}) {
  return (
    <div>
      <h2 className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        {label}
      </h2>
      <Suspense fallback={<Skeleton className="h-64 w-full rounded-lg" />}>
        <RelocationSummaryCard relocationId={relocationId} />
      </Suspense>
    </div>
  );
}
